import React, { useState, useEffect } from 'react';
import { praxisService } from '../../services/praxis.service';
import { Praxis, AppType } from '../../types';
import { useEnvironment } from '../../context/EnvironmentContext';

interface PraxisFormProps {
  praxis?: Praxis | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData {
  name: string;
  strasse: string;
  plz: string;
  stadt: string;
  telefon: string;
  email: string;
}

interface FormErrors {
  name?: string;
  email?: string;
}

const PraxisForm: React.FC<PraxisFormProps> = ({ praxis, onSuccess, onCancel }) => {
  const { currentApp } = useEnvironment();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    strasse: '',
    plz: '',
    stadt: '',
    telefon: '',
    email: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (praxis) {
      setFormData({
        name: praxis.name,
        strasse: praxis.strasse || '',
        plz: praxis.plz || '',
        stadt: praxis.stadt || '',
        telefon: praxis.telefon || '',
        email: praxis.email || '',
      });
    }
  }, [praxis]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const praxisData = {
        name: formData.name.trim(),
        strasse: formData.strasse.trim() || undefined,
        plz: formData.plz.trim() || undefined,
        stadt: formData.stadt.trim() || undefined,
        telefon: formData.telefon.trim() || undefined,
        email: formData.email.trim() || undefined,
        type: currentApp as AppType,
      };

      if (praxis) {
        await praxisService.updatePraxis(praxis.id, praxisData);
        setSuccessMessage('Praxis erfolgreich aktualisiert');
      } else {
        await praxisService.createPraxis(praxisData);
        setSuccessMessage('Praxis erfolgreich erstellt');
      }

      setTimeout(() => {
        onSuccess();
      }, 1000);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Fehler beim Speichern der Praxis');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">
        {praxis ? 'Praxis bearbeiten' : 'Neue Praxis'}
      </h2>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {errorMessage}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3 py-3 min-h-[44px] text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="z.B. Praxis Dr. Müller"
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name}</p>
          )}
        </div>

        <div className="mb-4">
          <label htmlFor="strasse" className="block text-sm font-medium text-gray-700 mb-1">
            Straße
          </label>
          <input
            type="text"
            id="strasse"
            name="strasse"
            value={formData.strasse}
            onChange={handleChange}
            className="w-full px-3 py-3 min-h-[44px] text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="z.B. Hauptstraße 123"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="plz" className="block text-sm font-medium text-gray-700 mb-1">
              PLZ
            </label>
            <input
              type="text"
              id="plz"
              name="plz"
              value={formData.plz}
              onChange={handleChange}
              className="w-full px-3 py-3 min-h-[44px] text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="z.B. 12345"
            />
          </div>

          <div>
            <label htmlFor="stadt" className="block text-sm font-medium text-gray-700 mb-1">
              Stadt
            </label>
            <input
              type="text"
              id="stadt"
              name="stadt"
              value={formData.stadt}
              onChange={handleChange}
              className="w-full px-3 py-3 min-h-[44px] text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="z.B. Berlin"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label htmlFor="telefon" className="block text-sm font-medium text-gray-700 mb-1">
              Telefon
            </label>
            <input
              type="tel"
              id="telefon"
              name="telefon"
              value={formData.telefon}
              onChange={handleChange}
              className="w-full px-3 py-3 min-h-[44px] text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="z.B. 030 12345678"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              E-Mail
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-3 min-h-[44px] text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="z.B. info@praxis.de"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-3 min-h-[44px] border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-3 min-h-[44px] bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Speichert...' : praxis ? 'Aktualisieren' : 'Erstellen'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PraxisForm;
